/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import { TUserTypeValue } from "pages/Users/Shared/BulkActions/Guest/ChangeUserType/constants/user-types";
import {
  ActionTypeView,
  CheckUsersStatus,
  SelectedActionType,
  SelectedTargetType,
} from "pages/Users/Shared/BulkActions/types/types";

export enum BulkActionActionType {
  RESET_STATE = "BULK_ACTION/RESET_STATE",
  SELECTED_TARGET = "BULK_ACTION/SELECTED_TARGET",
  ACTION_TYPE = "BULK_ACTION/ACTION_TYPE",
  ACTION_TYPE_VIEW = "BULK_ACTION/ACTION_TYPE_VIEW",
  IS_DROPDOWNS_VISIBLE = "BULK_ACTION/IS_DROPDOWNS_VISIBLE",
  UPDATE_VIEW = "BULK_ACTION/UPDATE_VIEW",
  SEND_INVITATION_STATE = "BULK_ACTION/SEND_INVITATION_STATE",
  ACCEPT_INVITATION_STATE = "BULK_ACTION/ACCEPT_INVITATION_STATE",
  REJECT_INVITATION_STATE = "BULK_ACTION/REJECT_INVITATION_STATE",
  SEND_REGISTRATION_LINK_STATE = "BULK_ACTION/SEND_REGISTRATION_LINK_STATE",
  CHANGE_DELETE_GUEST_OPTION = "BULK_ACTION/CHANGE_DELETE_GUEST_OPTION",
  CHANGE_USER_TYPE = "BULK_ACTION/CHANGE_USER_TYPE",
  SEND_EMAIL = "BULK_ACTION/SEND_EMAIL",
  CREATE_APPLICATION_STATUS = "BULK_ACTION/CREATE_APPLICATION_STATUS",
}

type SelectedTargetAction = {
  type: BulkActionActionType.SELECTED_TARGET;
  payload: SelectedTargetType;
};

type TypeViewAction = {
  type: BulkActionActionType.ACTION_TYPE_VIEW;
  payload: ActionTypeView;
};

type TypeShowDropdowns = {
  type: BulkActionActionType.IS_DROPDOWNS_VISIBLE;
  payload: boolean;
};

type ResetState = {
  type: BulkActionActionType.RESET_STATE;
};

type UpdateView = {
  type: BulkActionActionType.UPDATE_VIEW;
  payload: {
    actionTypeView?: ActionTypeView;
    actionType?: SelectedActionType;
    isDropdownsVisible?: boolean;
    checkUsersStatus?: CheckUsersStatus;
  };
};

type SendInvitationStateAction = {
  type: BulkActionActionType.SEND_INVITATION_STATE;
  payload: {
    sendInvitationTypes?: {
      email?: boolean;
      sms?: boolean;
    };
    sendInvitationDecision?: "ignore" | "create";
  };
};

type AcceptInvitationStateAction = {
  type: BulkActionActionType.ACCEPT_INVITATION_STATE;
  payload: {
    pending_only?: boolean;
    not_sent_only?: boolean;
    rejected_only?: boolean;
  };
};

type RejectInvitationStateAction = {
  type: BulkActionActionType.REJECT_INVITATION_STATE;
  payload: {
    pending_only?: boolean;
    not_sent_only?: boolean;
    accepted_only?: boolean;
  };
};

type SendRegistrationLinkStateAction = {
  type: BulkActionActionType.SEND_REGISTRATION_LINK_STATE;
  payload: {
    sendRegistrationLinkTypes?: {
      email?: boolean;
      sms?: boolean;
    };
  };
};

type ChangeDeleteGuestOption = {
  type: BulkActionActionType.CHANGE_DELETE_GUEST_OPTION;
  payload: {
    deleteGuestOption: "unlink" | "permanently";
  };
};

type ChangeUserType = {
  type: BulkActionActionType.CHANGE_USER_TYPE;
  payload: {
    userTypeOption?: TUserTypeValue;
  };
};

type SendEmailType = {
  type: BulkActionActionType.SEND_EMAIL;
  payload: {
    emailType?: string;
    sendEmailIsDisabled?: boolean;
    applyValidationRules?: boolean;
  };
};

type CreateApplicationStatus = {
  type: BulkActionActionType.CREATE_APPLICATION_STATUS;
  payload: {
    blinkIdStatus: "pending_review" | "approved";
  };
};

export type BulkActionAction =
  | SelectedTargetAction
  | TypeViewAction
  | TypeShowDropdowns
  | ResetState
  | UpdateView
  | ChangeDeleteGuestOption
  | SendInvitationStateAction
  | AcceptInvitationStateAction
  | RejectInvitationStateAction
  | SendRegistrationLinkStateAction
  | ChangeUserType
  | SendEmailType
  | CreateApplicationStatus;

export const selectActionTypeView = (
  data: TypeViewAction["payload"],
): TypeViewAction => ({
  type: BulkActionActionType.ACTION_TYPE_VIEW,
  payload: data,
});

export const selectTarget = (
  data: SelectedTargetAction["payload"],
): SelectedTargetAction => ({
  type: BulkActionActionType.SELECTED_TARGET,
  payload: data,
});

export const showDropdowns = (
  data: TypeShowDropdowns["payload"],
): TypeShowDropdowns => ({
  type: BulkActionActionType.IS_DROPDOWNS_VISIBLE,
  payload: data,
});

export const resetBulkActionState = (): ResetState => ({
  type: BulkActionActionType.RESET_STATE,
});

export const updateBulkActionView = (
  data: UpdateView["payload"],
): UpdateView => ({
  type: BulkActionActionType.UPDATE_VIEW,
  payload: data,
});

export const sendInvitationStateAction = (
  data: SendInvitationStateAction["payload"],
): SendInvitationStateAction => ({
  type: BulkActionActionType.SEND_INVITATION_STATE,
  payload: data,
});

export const acceptInvitationStateAction = (
  data: AcceptInvitationStateAction["payload"],
): AcceptInvitationStateAction => ({
  type: BulkActionActionType.ACCEPT_INVITATION_STATE,
  payload: data,
});

export const rejectInvitationStateAction = (
  data: RejectInvitationStateAction["payload"],
): RejectInvitationStateAction => ({
  type: BulkActionActionType.REJECT_INVITATION_STATE,
  payload: data,
});

export const sendRegistrationLinkStateAction = (
  data: SendRegistrationLinkStateAction["payload"],
): SendRegistrationLinkStateAction => ({
  type: BulkActionActionType.SEND_REGISTRATION_LINK_STATE,
  payload: data,
});

export const changeDeleteGuestOption = (
  data: ChangeDeleteGuestOption["payload"],
): ChangeDeleteGuestOption => ({
  type: BulkActionActionType.CHANGE_DELETE_GUEST_OPTION,
  payload: data,
});

export const changeUserType = (
  data: ChangeUserType["payload"],
): ChangeUserType => ({
  type: BulkActionActionType.CHANGE_USER_TYPE,
  payload: data,
});

export const sendEmail = (data: SendEmailType["payload"]): SendEmailType => ({
  type: BulkActionActionType.SEND_EMAIL,
  payload: data,
});

export const setCreateApplicationStatus = (
  data: CreateApplicationStatus["payload"],
): CreateApplicationStatus => ({
  type: BulkActionActionType.CREATE_APPLICATION_STATUS,
  payload: data,
});
